import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import CompanyHeader from '@/components/company/CompanyHeader';
import CompanySectionHeader from '@/components/company/CompanySectionHeader';
import FormBuilderWithLayout from '@/components/company/FormBuilderWithLayout';
import MainTable, { MainTableColumn } from '@/components/company/table/MainTable';
import GenerateAIModal from '@/components/company/GenerateAIModal';
import AIResultModal from '@/components/company/AIResultModal';
import BusinessGoalAIGenerateForm from './business-goal/BusinessGoalAIGenerateForm';
import {
  businessGoalAIGenerateSchema,
  type BusinessGoalAIGenerateFormData,
} from './business-goal/business-goal-ai-generate.schema';
import { useGenerateBusinessGoal } from './business-goal/useGenerateBusinessGoal';
import useCompanyState, { ICompanyField } from '@/states/company.state';
import useTenantState from '@/states/tenant.state';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';

interface BusinessGoal {
  year: number;
  revenue: number;
}

function extractBusinessGoals(businessGoal: unknown): BusinessGoal[] {
  if (typeof businessGoal === 'object' && businessGoal !== null && 'value' in businessGoal) {
    return (businessGoal as ICompanyField).value as BusinessGoal[];
  }
  if (Array.isArray(businessGoal)) {
    return businessGoal as BusinessGoal[];
  }
  return [];
}

export default function BusinessGoal() {
  const formControl = useForm<BusinessGoal>({
    defaultValues: { year: new Date().getFullYear(), revenue: 0 },
  });
  const { handleSubmit, reset } = formControl;
  const formRef = useRef<HTMLFormElement>(null);

  const { companyProfile, getCompanyInformation, updateCompanyField, isLoading } = useCompanyState();
  const { currentTenant } = useTenantState();

  const [isPublished, setIsPublished] = useState(false);
  const [draftGoals, setDraftGoals] = useState<BusinessGoal[]>([]);
  const [formMode, setFormMode] = useState<'add' | 'edit' | null>(null);
  const [selectedRow, setSelectedRow] = useState<BusinessGoal | null>(null);

  const [pageConfig, setPageConfig] = useState<DataConfig>({
    page: 0,
    limit: 10,
    orderBy: 'year',
    order: 'asc',
  });

  const aiFormControl = useForm<BusinessGoalAIGenerateFormData>({
    resolver: zodResolver(businessGoalAIGenerateSchema),
    defaultValues: {
      current_revenue: undefined,
      growth_target: undefined,
      business_type: '',
      market_size: '',
    },
  });

  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [generatedOptions, setGeneratedOptions] = useState<string[]>([]);
  const [parsedGoalOptions, setParsedGoalOptions] = useState<BusinessGoal[][]>([]);

  const generateBusinessGoalMutation = useGenerateBusinessGoal({
    tenantId: currentTenant?.id ? String(currentTenant.id) : '',
  });

  useEffect(() => {
    if (!currentTenant?.id) return;
    (async () => {
      const data = await getCompanyInformation(currentTenant.id);
      if (data) {
        const goals = extractBusinessGoals(data.business_goal);

        setDraftGoals(goals);
        setIsPublished(!!data.is_published_business_goal);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant?.id]);

  useEffect(() => {
    if (!companyProfile) return;

    const goals = extractBusinessGoals(companyProfile.business_goal);

    setDraftGoals(goals);
    setIsPublished(!!(companyProfile as { is_published_business_goal?: boolean })?.is_published_business_goal);
  }, [companyProfile]);

  const sortedDraftGoals = [...draftGoals].sort((a, b) => {
    const key = pageConfig.orderBy as keyof BusinessGoal;
    const dir = pageConfig.order === 'asc' ? 1 : -1;

    if (a[key] < b[key]) return -1 * dir;
    if (a[key] > b[key]) return 1 * dir;
    return 0;
  });

  const handleSaveAll = async () => {
    if (!currentTenant?.id) return;
    try {
      await updateCompanyField(currentTenant.id, 'business_goal', draftGoals, isPublished);
      const updatedData = await getCompanyInformation(currentTenant.id);
      if (updatedData) {
        const goals = extractBusinessGoals(updatedData.business_goal);
        setDraftGoals(goals);
        setIsPublished(!!updatedData.is_published_business_goal);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to save business goals');
    }
  };

  const handlePublish = async (value: boolean) => {
    if (!currentTenant?.id) return;
    try {
      await updateCompanyField(currentTenant.id, 'business_goal', draftGoals, value);

      const updatedData = await getCompanyInformation(currentTenant.id);
      if (updatedData) {
        const goals = extractBusinessGoals(updatedData.business_goal);
        setDraftGoals(goals);
        setIsPublished(!!updatedData.is_published_business_goal);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to update publish status');
    }
  };

  const handleSaveDraft = (data: BusinessGoal) => {
    const parsedData = {
      ...data,
      year: Number(data.year),
      revenue: Number(data.revenue),
    };

    let updated = [...draftGoals];

    if (formMode === 'edit' && selectedRow) {
      updated = updated.map(g => (g.year === selectedRow.year ? { ...g, ...parsedData } : g));
    } else {
      const exist = updated.some(g => g.year === parsedData.year);
      if (exist) {
        toast.error(`Year ${parsedData.year} already exists. Please use a different year.`);
        return;
      }

      updated.push(parsedData);
    }

    setDraftGoals(updated);
    setFormMode(null);
    setSelectedRow(null);
  };

  const handleDeleteDraft = (row: BusinessGoal) => {
    const filtered = draftGoals.filter(g => g.year !== row.year);
    setDraftGoals(filtered);
  };

  const handleGenerate = async (data: BusinessGoalAIGenerateFormData) => {
    try {
      const result = await generateBusinessGoalMutation.mutateAsync(data);

      if (result.success && result.data?.generated_options) {
        const parsed = result.data.generated_options.map((option: string) => {
          try {
            const jsonData = JSON.parse(option);
            return Object.entries(jsonData).map(([year, revenue]) => ({
              year: parseInt(year, 10),
              revenue: parseInt(String(revenue).replace(/\./g, ''), 10),
            }));
          } catch (e) {
            console.error('Failed to parse business goal option:', e);
            return [];
          }
        });

        setParsedGoalOptions(parsed);

        const displayOptions = parsed.map((goals: BusinessGoal[]) => {
          const sortedGoals = [...goals].sort((a, b) => a.year - b.year);
          return sortedGoals.map(g => `**${g.year}:** Rp ${g.revenue.toLocaleString('id-ID')}`).join('\n\n');
        });

        setGeneratedOptions(displayOptions);
        setIsGenerateModalOpen(false);
        setIsResultModalOpen(true);
        toast.success('Business goals generated successfully');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to generate business goals');
    }
  };

  const handleOpenGenerateModal = () => {
    aiFormControl.reset();
    setIsGenerateModalOpen(true);
  };

  const handleCloseGenerateModal = () => {
    setIsGenerateModalOpen(false);
  };

  const handleCloseResultModal = () => {
    setIsResultModalOpen(false);
  };

  const handleSaveGeneratedGoals = async (selectedOption: string) => {
    // Find index of selected option
    const selectedIndex = generatedOptions.indexOf(selectedOption);
    if (selectedIndex === -1) return;

    const selectedGoals = parsedGoalOptions[selectedIndex];
    if (!selectedGoals || selectedGoals.length === 0) return;

    const sortedGoals = [...selectedGoals].sort((a, b) => a.year - b.year);

    setDraftGoals([...sortedGoals]);
    setIsResultModalOpen(false);
    toast.success('Business goals added to draft');

    if (!currentTenant?.id) return;

    try {
      await updateCompanyField(currentTenant.id, 'business_goal', sortedGoals, isPublished);

      const updatedData = await getCompanyInformation(currentTenant.id);
      if (updatedData) {
        const goals = extractBusinessGoals(updatedData.business_goal);
        setDraftGoals(goals);
        setIsPublished(!!updatedData.is_published_business_goal);
      }

      toast.success('Business goals saved to profile successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to save business goals');
    }
  };

  const formatRupiah = (value: number | string | null | undefined) => {
    const num = Number(value);
    if (isNaN(num)) return '-';
    return `Rp ${num.toLocaleString('id-ID')}`;
  };

  const columns: MainTableColumn[] = [
    { name: 'year', label: 'Year' },
    {
      name: 'revenue',
      label: 'Revenue',
      render: value => {
        return formatRupiah(value);
      },
    },
  ];

  const fields = [
    {
      name: 'year',
      label: 'Year',
      type: 'number',
      required: true,
      placeholder: 'e.g. 2025',
      inputProps: { valueAsNumber: true },
    },
    {
      name: 'revenue',
      label: 'Revenue',
      type: 'number',
      required: true,
      placeholder: 'e.g. 12000000',
      inputProps: { valueAsNumber: true },
    },
  ];

  return (
    <section className="w-full">
      <CompanyHeader
        title="Business Goals"
        description="Define yearly revenue targets for your company."
        saveLabel="Save Business Goals"
        onSave={handleSaveAll}
        onPublish={handlePublish}
        isSaving={isLoading}
        isPublished={isPublished}
      />

      <div className="mx-5 border-b border-gray-200" />

      <CompanySectionHeader
        title="Revenue Targets"
        onAddTarget={() => {
          reset({ year: undefined, revenue: undefined });
          setFormMode('add');
        }}
        onGenerate={handleOpenGenerateModal}
        generateLabel="Generate Goals"
      />

      {formMode && (
        <FormProvider {...formControl}>
          <form ref={formRef} onSubmit={handleSubmit(handleSaveDraft)} className="mt-4">
            <FormBuilderWithLayout
              description={formMode === 'add' ? 'Add Business Goal' : 'Edit Business Goal'}
              fields={fields}
              initialPublished={isPublished}
              onPublishChange={setIsPublished}
              onSubmit={handleSubmit(handleSaveDraft)}
              onCancel={() => {
                setFormMode(null);
                setSelectedRow(null);
              }}
              submitLabel={formMode === 'add' ? 'Create' : 'Update'}
              cancelLabel="Cancel"
            />
          </form>
        </FormProvider>
      )}

      <MainTable
        showHeader={false}
        isLoading={isLoading}
        data={{
          content: sortedDraftGoals as unknown as { [key: string]: unknown }[],
          totalElements: sortedDraftGoals.length,
          totalPages: 1,
        }}
        columns={columns}
        pageConfig={pageConfig}
        setPageConfig={setPageConfig}
        onEdit={row => {
          reset(row);
          setFormMode('edit');
          setSelectedRow(row);
        }}
        onDelete={handleDeleteDraft}
        showActions
        useConfirmDelete={false}
      />

      <GenerateAIModal
        isOpen={isGenerateModalOpen}
        onClose={handleCloseGenerateModal}
        onSubmit={aiFormControl.handleSubmit(handleGenerate)}
        title="Generate Business Goals with AI"
        description="Provide your current financial information to help AI project future revenue targets."
        generateButtonLabel="Generate Goals"
        isLoading={generateBusinessGoalMutation.isLoading}
      >
        <BusinessGoalAIGenerateForm
          control={aiFormControl.control}
          errors={aiFormControl.formState.errors}
          isLoading={generateBusinessGoalMutation.isLoading}
        />
      </GenerateAIModal>

      <AIResultModal
        isOpen={isResultModalOpen}
        onClose={handleCloseResultModal}
        options={generatedOptions}
        onSave={handleSaveGeneratedGoals}
        title="AI Generated Business Goals"
        description="Select the revenue projection that best fits your business strategy"
        saveButtonLabel="Save to Profile"
        isLoading={isLoading}
      />
    </section>
  );
}
